import { useEffect, useMemo } from 'react';

import { useAccountStore, useProjectsStore } from '@/store';
import { hasBrandStrategyContent } from '@/V2/features/brand-strategy/utils/strategy-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TProjectStatus } from '@/types/projects.ts';
import { QueryKey } from '@/types/queryKey.ts';
import {
  generatePost,
  generatePostIdeas,
  getBrandWiki,
  getPostIdeas,
  getProjects,
  putBrandWikiSection,
} from '@/lib/services/projects';
import useProjectId from '@/hooks/useProjectId.ts';

export function useBrandWiki() {
  const projectId = useProjectId();

  return useQuery({
    queryKey: [QueryKey.BrandWiki, projectId, hasBrandStrategyContent],
    queryFn: () => getBrandWiki(projectId),
    enabled: !!(projectId && hasBrandStrategyContent),
  });
}

export function useGeneratePost() {
  return useMutation({
    mutationFn: generatePost,
  });
}

export function useGeneratePostIdeas() {
  return useMutation({
    mutationFn: generatePostIdeas,
  });
}

export function usePostIdeas(projectId: string, isLoading?: boolean) {
  return useQuery({
    queryKey: [QueryKey.PostIdeas],
    queryFn: () => getPostIdeas(projectId!),
    enabled: !!projectId && !isLoading,
  });
}

export function useSaveWikiSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putBrandWikiSection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.BrandWiki] });
    },
  });
}

const useProjects = () => {
  const setProjects = useProjectsStore((s) => s.setProjects);
  const projects = useProjectsStore((s) => s.projects);
  const defaultProject = useAccountStore((state) => state.defaultProject);
  const isItMarketer = useAccountStore((state) => state.isItMarketer);

  const { data, isLoading } = useQuery({
    queryKey: [QueryKey.Clients],
    queryFn: () => getProjects(),
    structuralSharing: false,
    enabled: isItMarketer,
  });

  useEffect(() => {
    if (isLoading || !data || !defaultProject) return;
    const clientsList = data.data || [];
    const defaultProjectInTheList = clientsList.find(
      (c) => defaultProject.id === c.id
    );

    let localClients = [];
    if (!isItMarketer) {
      localClients = [defaultProject];
    } else if (defaultProjectInTheList) {
      localClients = clientsList;
    } else {
      localClients = [
        ...clientsList,
        {
          ...defaultProject,
          status: TProjectStatus.ACTIVE,
        },
      ];
    }
    setProjects(localClients);
  }, [data, isLoading, setProjects, defaultProject]);

  const projectsExist = useMemo(
    () => !!data?.data?.length,
    [data?.data?.length]
  );

  return {
    projects,
    projectsExist,
    isLoading,
  };
};

export default useProjects;
